import { useState } from 'react';
import { Link } from 'react-router-dom';
import PhaseTriggerForm from '../chapters/PhaseTriggerForm';
import ProcessingProgressBar from '../ProcessingProgressBar';

/**
 * BlitzNotReadyView — full-page Blitz route when Phase 3 has not produced
 * key-points yet (FR-010 / spec 010).
 *
 * Renders the flat chapter-title list and, for owners, one of:
 *   - a "Generate Blitz" CTA (PhaseTriggerForm phase="blitz") when no Phase 3
 *     run is active and none is auto-queued, OR
 *   - a progress bar + "Starting…" notice immediately after the owner submits
 *     the trigger (optimistic UI while the first poll reflects the new state),
 *   - a non-interactive "Blitz is being generated" notice with a link back
 *     to the Chapters page when a Phase 3 run is in flight / scheduled.
 *
 * The player hook is intentionally NOT mounted from this view — Phase 3 has
 * not produced summary or per-key-point audio, so building a queue would
 * either be empty or fall back to URLs that 404.
 *
 * Props:
 *   - fileId, fileTitle: strings used in chrome + the trigger form.
 *   - chapters: array of `{ id, title, order, ... }` from `GET /files/{id}`.
 *   - phase3Status: `not_started` / `pending` / `in_progress` / `done` /
 *     `complete` / `failed` / null.
 *   - phase2Status: same enum; used to detect a queued auto-run after
 *     Phase 2 finishes.
 *   - autoPhase3: bool (per-file snapshot of the owner's `automatic_blitz`).
 *   - isOwner: bool — gates the CTA / in-flight notice (non-owners see only
 *     the chapter list).
 *   - onExit: optional () => void — wires the back-arrow to the parent's
 *     navigation behaviour.
 *   - blitzStageState (optional): live `blitz` stage state from the polling
 *     snapshot (`pending` / `in_progress` / `complete` / `failed` / null).
 *   - progressSnapshot (optional): full ProgressSnapshotResponse; passed to
 *     ProcessingProgressBar while blitz is starting / in-flight.
 */
const isDoneStatus = (s) => s === 'done' || s === 'complete';
const isActiveStatus = (s) => s === 'in_progress' || s === 'pending' || s === 'running';

function BlitzNotReadyView({
  fileId,
  fileTitle,
  chapters,
  phase3Status,
  phase2Status,
  autoPhase3,
  isOwner,
  onExit,
  blitzStageState = null,
  progressSnapshot = null,
}) {
  const [blitzTriggered, setBlitzTriggered] = useState(false);

  const phase3Settled =
    isDoneStatus(phase3Status) ||
    blitzStageState === 'complete';
  const inFlight =
    !phase3Settled &&
    (
      isActiveStatus(phase3Status) ||
      isActiveStatus(blitzStageState) ||
      (autoPhase3 === true && phase3Status === 'not_started' && isDoneStatus(phase2Status))
    );
  const failed = !phase3Settled && phase3Status === 'failed';
  const safeChapters = Array.isArray(chapters) ? chapters : [];

  const handleTriggerSubmitted = (result) => {
    if (result.kind === 'accepted') setBlitzTriggered(true);
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col" data-testid="blitz-not-ready-view">
      <header className="bg-gray-800 border-b border-gray-700 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onExit}
              className="p-2 rounded-lg hover:bg-gray-700 transition-colors"
              title="Back to Chapters"
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-white font-semibold truncate max-w-xs sm:max-w-md">{fileTitle}</h1>
              <p className="text-xs text-purple-400">⚡ Blitz Mode</p>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto w-full p-4">
        {isOwner && inFlight && (
          <div
            role="status"
            data-testid="blitz-in-flight-notice"
            className="mb-4 rounded-lg p-4 border bg-blue-900/30 border-blue-700 text-blue-100"
          >
            <p className="text-sm font-semibold mb-1">Blitz is being generated</p>
            <p className="text-sm leading-snug">
              Key points and Blitz audio are still being produced. You can follow stage-level
              progress on the Chapters page; the player will become available once the run finishes.
            </p>
            <p className="mt-2 text-sm">
              <Link to={`/projects/${fileId}/chapters`} className="underline text-blue-200 hover:text-white">
                Open Chapters &amp; processing status
              </Link>
            </p>
          </div>
        )}

        {isOwner && !inFlight && !phase3Settled && (
          blitzTriggered ? (
            <div
              role="status"
              data-testid="blitz-starting-notice"
              className="mb-4 rounded-lg p-4 border bg-blue-900/30 border-blue-700 text-blue-100"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-400 border-t-transparent shrink-0" />
                <p className="text-sm font-semibold">Starting Blitz generation…</p>
              </div>
              {progressSnapshot && (
                <ProcessingProgressBar snapshot={progressSnapshot} />
              )}
            </div>
          ) : (
            <div className="mb-4" data-testid="blitz-generate-cta">
              {failed && (
                <p className="mb-2 text-sm text-red-300">
                  The previous Blitz run did not finish. You can retry it below.
                </p>
              )}
              {!failed && (
                <p className="mb-2 text-sm text-gray-300">
                  Blitz hasn't been generated for this file yet. Start it to get per-chapter
                  key points and audio.
                </p>
              )}
              <PhaseTriggerForm phase="blitz" fileId={fileId} onSubmitted={handleTriggerSubmitted} />
            </div>
          )
        )}

        <section
          aria-label="Chapters"
          className="bg-gray-800/50 border border-gray-700 rounded-lg overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-gray-700">
            <h2 className="text-sm font-semibold text-gray-200">
              Chapters ({safeChapters.length})
            </h2>
          </div>
          {safeChapters.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-400">No chapters available yet.</p>
          ) : (
            <ol className="divide-y divide-gray-700">
              {safeChapters.map((c, i) => (
                <li key={c.id || i} className="px-4 py-3 flex items-start gap-3">
                  <span className="text-xs text-gray-500 tabular-nums mt-0.5 w-6 shrink-0">
                    {i + 1}.
                  </span>
                  <span className="text-sm text-gray-100">
                    {c.title || `Chapter ${i + 1}`}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </main>
    </div>
  );
}

export default BlitzNotReadyView;
